import { PageHeader, Stat, Stats, DataTable } from '../../components/office';
import { useSite } from '../../contexts/SiteContext';
import { useEffect, useState } from 'react';
import { Button } from '@verlo/shared/components/ui/button';
import { Input } from '@verlo/shared/components/ui/input';
import { Label } from '@verlo/shared/components/ui/label';
import { Segmented } from '@verlo/shared/components/ui/segmented';
import { Signal } from '@verlo/shared/components/ui/signal';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from '@verlo/shared/components/ui/dialog';
import { Plus } from 'lucide-react';
import { toast } from 'sonner';
import { sitesApi } from '@verlo/shared/services/api';

// Sites (Back office sweep, Oct 2026). Every site you run, in one table: where it is, today's
// hours, and whether Verlo is on its floor or only in its office. Adding a site is parked
// (D13): what a new site means for the agreement, its till and its data needs its own plan.

const todayKey = () => ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'][new Date().getDay()];
const emptyForm = { name: '', city: '', timezone: 'Europe/London', plan: 'full' };

// What a site bought. `back_office` is a venue still inside somebody else's EPOS
// contract: they send us their sales, and everything till-shaped is hidden from them
// rather than shown empty (`areas.js`, `RequireTill`).
const PLANS = [
  { value: 'full', label: 'Floor & office' },
  { value: 'back_office', label: 'Office only' },
];

const SitesPage = () => {
  const { refresh: refreshSwitcher } = useSite();   // the header's site switcher follows edits here
  const [sites, setSites] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [retiring, setRetiring] = useState(null);

  const load = async () => {
    try {
      const res = await sitesApi.list();
      setSites(Array.isArray(res.data) ? res.data : (res.data?.sites || []));
    } catch {
      setSites([]);
      toast.error('Could not load sites');
    }
  };
  useEffect(() => { load(); }, []);

  const openAdd = () => { setEditingId(null); setForm(emptyForm); setDialogOpen(true); };
  const openEdit = (s) => { setEditingId(s.id); setForm({ name: s.name, city: s.city || '', timezone: s.timezone || 'Europe/London', plan: s.plan || 'full' }); setDialogOpen(true); };

  const save = async () => {
    if (!form.name.trim()) { toast.error('Give the site a name'); return; }
    setSaving(true);
    const payload = { name: form.name.trim(), city: form.city.trim(), timezone: form.timezone, plan: form.plan };
    try {
      if (editingId) await sitesApi.update(editingId, payload);
      else await sitesApi.create(payload);
      toast.success(editingId ? 'Site saved' : 'Site added');
      setDialogOpen(false);
      await load();
      refreshSwitcher();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save the site");
    } finally {
      setSaving(false);
    }
  };

  const retire = async () => {
    const s = retiring;
    setRetiring(null);
    try {
      await sitesApi.delete(s.id);
      toast.success(`${s.name} retired`);
      await load();
      refreshSwitcher();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't retire the site");
    }
  };

  const all = sites || [];
  const onFloor = all.filter((s) => s.plan !== 'back_office');
  const columns = [
    { key: 'name', label: 'Site', sortable: true, render: (s) => (
      <div className="flex items-center gap-2 font-medium text-ink">{s.name}{s.is_primary && <Signal tone="info" dot={false}>Primary</Signal>}</div>
    ) },
    { key: 'city', label: 'Where', muted: true, sortable: true, render: (s) => [s.city, s.timezone].filter(Boolean).join(' · ') || <span className="text-ink-faint">—</span> },
    { key: 'today', label: 'Today', muted: true, render: (s) => {
      const h = s.service_hours?.[todayKey()];
      return h ? `${h.open}–${h.close}` : <span className="text-ink-faint">—</span>;
    } },
    { key: 'plan', label: 'Verlo', render: (s) => (
      // A back-office site has no node by design, so "No till yet" is the one thing it
      // must not say to it.
      s.plan === 'back_office' ? <Signal tone="info" dot={false}>office only</Signal>
        : s.node_id ? <Signal tone="ok" dot={false}>till live</Signal>
          : <Signal tone="warn" dot={false}>no till yet</Signal>
    ) },
  ];

  return (
    <div className="space-y-5" data-testid="sites-page">
      <PageHeader
        title="Sites"
        purpose="Every site you run, and which tills belong to it."
        actions={<Button className="bg-verlo hover:bg-verlo-hover" onClick={openAdd} data-testid="site-add"><Plus size={16} className="mr-2" />Add site</Button>}
      />

      <Stats>
        <Stat label="Sites" value={sites ? all.length : null} testId="sites-count" />
        <Stat label="Verlo on the floor" value={sites ? onFloor.length : null} hint={sites ? `${onFloor.filter((s) => s.node_id).length} with a till live` : undefined} />
        <Stat label="Office only" value={sites ? all.length - onFloor.length : null} hint="On their own till for now" />
      </Stats>

      <DataTable
        columns={columns} rows={all} rowKey="id" loading={sites === null}
        defaultSort={{ key: 'name', dir: 'asc' }}
        onRowClick={openEdit}
        rowTestId={(s) => `site-row-${s.id}`}
        actions={(s) => (
          <>
            <Button variant="ghost" size="sm" className="h-8" onClick={() => openEdit(s)} data-testid={`site-edit-${s.id}`}>Edit</Button>
            {!s.is_primary && <Button variant="ghost" size="sm" className="h-8 text-signal-stop hover:text-signal-stop" onClick={() => setRetiring(s)} data-testid={`site-delete-${s.id}`}>Retire</Button>}
          </>
        )}
        empty={{ word: 'Sites', title: 'No sites yet', testId: 'sites-empty' }}
        testId="sites-table"
      />

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent size="form" data-testid="site-dialog">
          <DialogHeader>
            <DialogTitle>{editingId ? form.name || 'Site' : 'Add a site'}</DialogTitle>
            <DialogDescription>Its name, where it is, and whether Verlo runs its floor too.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-[12.5px] text-ink-soft">Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. High Street, Leeds" data-testid="site-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-[12.5px] text-ink-soft">Town or city</Label>
                <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="site-city-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[12.5px] text-ink-soft">Timezone</Label>
                <Input value={form.timezone} onChange={(e) => setForm({ ...form, timezone: e.target.value })} data-testid="site-tz-input" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-[12.5px] text-ink-soft">Verlo runs</Label>
              <Segmented value={form.plan} onChange={(plan) => setForm({ ...form, plan })} options={PLANS} stretch data-testid="site-plan" />
              <p className="text-[12.5px] text-ink-soft">
                {form.plan === 'back_office'
                  ? 'Service runs on their current till; they send us the sales. The till screens stay hidden until they add the floor.'
                  : 'Verlo on the floor and in the office.'}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button className="bg-verlo hover:bg-verlo-hover" onClick={save} disabled={saving} data-testid="site-save">
              {saving ? 'Saving…' : editingId ? 'Save' : 'Add site'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!retiring} onOpenChange={(o) => !o && setRetiring(null)}>
        <DialogContent size="confirm">
          <DialogHeader>
            <DialogTitle>Retire {retiring?.name}?</DialogTitle>
            <DialogDescription>It goes from the site list and the header's site picker. Nothing it recorded is deleted.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRetiring(null)}>Keep it</Button>
            <Button variant="destructive" onClick={retire} data-testid="site-retire-confirm">Retire</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SitesPage;
